import type { AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';

export const ASSISTANT_ATTACHMENT_ACCEPT = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/json',
  'application/json',
].join(',');

export const ASSISTANT_ATTACHMENT_MAX_FILES = 3;
export const ASSISTANT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ASSISTANT_ATTACHMENT_MAX_TOTAL_BYTES = 8 * 1024 * 1024;

const ALLOWED_MEDIA_TYPES = new Set(ASSISTANT_ATTACHMENT_ACCEPT.split(','));
const MULTIMODAL_MEDIA_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('The selected file could not be read'));
    reader.readAsDataURL(file);
  });
}

export const assistantAttachmentAdapter: AttachmentAdapter = {
  accept: ASSISTANT_ATTACHMENT_ACCEPT,
  async add({ file }): Promise<PendingAttachment> {
    if (!ALLOWED_MEDIA_TYPES.has(file.type)) throw new Error('This file type is not supported');
    if (file.size > ASSISTANT_ATTACHMENT_MAX_BYTES) throw new Error('Files must be 5 MB or smaller');
    return {
      id: crypto.randomUUID(),
      type: file.type.startsWith('image/') ? 'image' : 'document',
      name: file.name,
      contentType: file.type,
      file,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  },
  async send(attachment): Promise<CompleteAttachment> {
    return {
      ...attachment,
      status: { type: 'complete' },
      content: [{
        type: 'file',
        data: await fileToDataUrl(attachment.file),
        mimeType: attachment.contentType ?? 'application/octet-stream',
        filename: attachment.name,
      }],
    };
  },
  async remove() {
    // Files are kept only in the local composer until the user sends them.
  },
};

export function validatePendingAssistantAttachments(
  attachments: ReadonlyArray<{ file?: File }>,
): string | null {
  if (attachments.length > ASSISTANT_ATTACHMENT_MAX_FILES) {
    return `Attach at most ${ASSISTANT_ATTACHMENT_MAX_FILES} files`;
  }
  const totalBytes = attachments.reduce((total, attachment) => total + (attachment.file?.size ?? 0), 0);
  if (totalBytes > ASSISTANT_ATTACHMENT_MAX_TOTAL_BYTES) {
    return 'Attachments must total 8 MB or less';
  }
  return null;
}

type FileLikePart = { type?: unknown; url?: unknown; mediaType?: unknown; filename?: unknown };

function parseDataUrl(url: string): { mediaType: string; bytes: number } | null {
  const match = /^data:([^;,]+);base64,([A-Za-z0-9+/]*={0,2})$/.exec(url);
  if (!match) return null;
  const payload = match[2];
  if (payload.length % 4 !== 0) return null;
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return { mediaType: match[1], bytes: (payload.length / 4) * 3 - padding };
}

export function validateAssistantFileParts(parts: readonly unknown[]): string | null {
  const files = parts.filter((part): part is FileLikePart => Boolean(part && typeof part === 'object' && (part as FileLikePart).type === 'file'));
  if (files.length > ASSISTANT_ATTACHMENT_MAX_FILES) return `Attach at most ${ASSISTANT_ATTACHMENT_MAX_FILES} files`;
  let totalBytes = 0;
  for (const file of files) {
    if (typeof file.url !== 'string' || !file.url.startsWith('data:')) return 'Attachments must be local data files';
    if (typeof file.mediaType !== 'string' || !ALLOWED_MEDIA_TYPES.has(file.mediaType)) return 'Unsupported attachment type';
    if (typeof file.filename !== 'string' || !file.filename.trim() || file.filename.length > 180) return 'Invalid attachment filename';
    const parsed = parseDataUrl(file.url);
    if (!parsed) return 'Invalid attachment data';
    if (parsed.mediaType !== file.mediaType) return 'Attachment type does not match its data';
    if (parsed.bytes > ASSISTANT_ATTACHMENT_MAX_BYTES) return 'Files must be 5 MB or smaller';
    totalBytes += parsed.bytes;
  }
  if (totalBytes > ASSISTANT_ATTACHMENT_MAX_TOTAL_BYTES) return 'Attachments must total 8 MB or less';
  return null;
}

export function requiredAssistantModelKeyForFileParts(parts: readonly unknown[]): 'multimodal' | null {
  return parts.some((part) => {
    if (!part || typeof part !== 'object') return false;
    const file = part as FileLikePart;
    return file.type === 'file' && typeof file.mediaType === 'string' && MULTIMODAL_MEDIA_TYPES.has(file.mediaType);
  }) ? 'multimodal' : null;
}
